/**
 * Free courses by sign-up link.
 *
 * An admin switches a course on as "free via link" (courses.free_via_link). An ad
 * link carries that course's ClickUp id as `?course=`; it is saved with the other
 * signup params, for brand-new accounts only (fn_record_signup_params). From then
 * on that person — and nobody else through this rule — can read the course, its
 * modules, lessons, videos and files (the `*_select_via_link` RLS policies).
 *
 * Every lookup here resolves server-side from the caller's own session; the client
 * never says which course it is entitled to.
 */
import { useQuery } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/use-auth';

/** What rpc_my_campaign_landing returns for the signed-in caller. */
export interface CampaignLanding {
  /** The caller signed up with a `course=` link (any value). */
  is_lead: boolean;
  /** The course they may open: switched on, published, public. Null otherwise. */
  course_id: string | null;
}

async function fetchCampaignLanding(): Promise<CampaignLanding | null> {
  const { data, error } = await supabase.rpc('rpc_my_campaign_landing');
  if (error) throw error;
  const row = (Array.isArray(data) ? data[0] : data) as CampaignLanding | undefined;
  return row ?? null;
}

/**
 * For the root guard. NEVER throws: any failure is `null`, which the guard treats
 * as "not a lead" and sends to /onboarding as it always has.
 *
 * Keyed per user. A found course is cached for the session; anything else is
 * re-checked, so a course saved a moment after sign-in (the OAuth callback) or
 * switched on later is seen next time.
 */
export async function getMyCampaignLanding(
  queryClient: QueryClient,
  userId: string,
): Promise<CampaignLanding | null> {
  try {
    const key = ['campaign-landing', userId];
    const cached = queryClient.getQueryData<CampaignLanding | null>(key);
    if (cached?.course_id) return cached;
    const fresh = await fetchCampaignLanding();
    // Only a found course is cached: "lead, no course yet" is re-checked so a course
    // an admin switches on later is picked up in the same tab.
    if (fresh?.course_id) queryClient.setQueryData(key, fresh);
    return fresh;
  } catch (err) {
    console.error('[campaign-landing] lookup failed', err);
    return null;
  }
}

/** The course this user may open through their sign-up link, or null. */
export function useMyLinkCourseId(): string | null {
  const { user } = useAuth();
  const { data } = useQuery({
    queryKey: ['campaign-landing', user?.id ?? 'anon'],
    queryFn: fetchCampaignLanding,
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  return data?.course_id ?? null;
}

/** One row of the admin "Free courses" table. */
export interface LinkCourseStats {
  course_id: string;
  clickup_id: string;
  name: string;
  access_level: string | null;
  status: string;
  /** Global and public — the only courses that can be given away by link. */
  eligible: boolean;
  free_via_link: boolean;
  /** New accounts made from the course's link. */
  signups: number;
  /** Of those new accounts, confirmed (email or Google/Microsoft/Facebook). */
  confirmed: number;
  /** Everyone holding the course (new accounts + added) who has started / finished it. */
  started: number;
  finished: number;
  /** Existing accounts that added the course with "Add to my account" (migration 686). */
  added: number;
}

/** Every course with its link toggle and sign-up numbers. Super admins only (server-checked). */
export async function fetchLinkCourseStats(): Promise<LinkCourseStats[]> {
  const { data, error } = await supabase.rpc('admin_link_course_stats');
  if (error) throw error;
  return ((data ?? []) as LinkCourseStats[]).map((r) => ({
    ...r,
    signups: Number(r.signups ?? 0),
    confirmed: Number(r.confirmed ?? 0),
    started: Number(r.started ?? 0),
    finished: Number(r.finished ?? 0),
    added: Number(r.added ?? 0),
  }));
}

/** Switch a course's "free via link" on or off. Super admins only; logged server-side. */
export async function setCourseFreeViaLink(courseId: string, on: boolean): Promise<void> {
  const { error } = await supabase.rpc('admin_set_course_free_via_link', {
    p_course_id: courseId,
    p_on: on,
  });
  if (error) throw error;
}

/**
 * The Alfie part of the ad link for a course. The funnel appends its own
 * `&email=…&full_name=…&fbclid=…` exactly as it does for every sign-up today.
 */
export function buildCourseSignupLink(origin: string, clickupId: string): string {
  return `${origin}/signup?plan=free&course=${encodeURIComponent(clickupId)}`;
}
